<template>
  <Body>
    <LayoutAppNavBar
      v-if="layoutStore.isNavigationHeaderVisible"
      :languages="navBarItems.languages"
      :nav-items="navBarItems.navigationItems"
      :auth="{
        firstName: userStore.firstName,
        isAuthenticated,
        loginWithRedirect,
        logout: logoutHandler,
      }"
    />

    <main id="main-content">
      <LazySnackbar />
      <slot />
    </main>

    <LazyLayoutAppFooter
      v-if="layoutStore.isFooterVisible && footerData"
      :legal-links="footerData.legalLinks"
      :disclaimer="footerData.disclaimer"
      :social-links="footerData.showSocialLinks"
      :navigation-groups="footerData.footerNavigation"
    />
  </Body>
</template>
<script setup lang="ts">
const { locale } = useI18n();
const userStore = useUserStore();
const { $auth } = useNuxtApp();
const { logout, isAuthenticated, loginWithRedirect } = $auth;
const localePath = useLocalePath();
const { navBarItems } = useNavigationItems();
const { navigationState } = useNavigation();
const isScrollLocked = useScrollLock(window?.document);

watch<TNavigationState>(
  navigationState.value,
  (newValue) => {
    isScrollLocked.value = !!(
      newValue.mobileDrawerOpen || newValue.openedNavigationDropDownId
    );
  },
  { immediate: false },
);

useHead({
  htmlAttrs: {
    lang: locale.value,
  },
});

const { setHotels } = useHotels();
const { data } = await useFetch<PropertyHotel[]>("/api/v2/properties/hotels", {
  query: { locale },
});
setHotels(data);

const { data: footerData } = await useFetch<FooterSkeleton["fields"]>(
  "/api/v2/footer",
  {
    query: { locale },
    onResponseError({ response }) {
      const { status: statusCode, statusText: message } = response;

      throw showError({
        statusCode,
        message,
      });
    },
  },
);

const layoutStore = useLayoutStore();

function logoutHandler() {
  if (import.meta.client) {
    // Clear redfast data on explicit user logout
    localStorage.removeItem("redfast-id");
    localStorage.removeItem("redfast-user-email");

    userStore.logoutUser();
    logout({
      logoutParams: {
        returnTo: window.location.origin + localePath("/"), // Go to homepage with a message
      },
    });
  }
}

if (import.meta.client) {
  window.cmtr;
}
</script>

<style lang="scss" scoped>
header {
  position: fixed;
  top: 0;
  z-index: 33;
  width: 100%;
  height: var(--header-navigation-height-mobile, 60px);
  margin: 0;
  background-color: var(--primary-white);

  @include xl {
    height: var(--header-navigation-height, 80px);
  }
}

#transparent-black {
  position: fixed;
  z-index: 15;
  width: 100vw;
  height: 100vh;
  background-color: rgb(0 0 0 / 33%);
}

.shadow {
  box-shadow: var(--shadow);
  transition: 0.3s;
}

#main-content {
  min-height: 95vh;
}
</style>
